import {AfterViewInit, ChangeDetectorRef, Component, ViewChild} from "@angular/core";
import {
    AuthService,
    LocalConfigurationService,
    provideEntityModel,
    provideSearchModel,
    ValidationMessage
} from "@fedelta-pos/data-client";
import {IdentityModel, LoginResult, LoginStatus, VerificationCodeDeliveryMethod} from "../identity.model";
import {SessionModel} from "../../orchestration/session.model";
import {ResponsiveService} from "../../orchestration/responsive.service";
import {NavigationService} from "../../orchestration/navigation.service";
import {LoadingService} from "../../ui-elements/loading/loading.service";
import {SessionController} from "../../orchestration/session.controller";
import {Page} from "../../orchestration/page";
import {IdentificationMode} from "./identification-mode";
import {Local} from "../../config-support/config";
import {MessageLevel} from "../../notification/message/message";
import {FormComponent} from "../../inputs/form/form.component";
import {NavigationParams} from "../../orchestration/navigation-params";
import {AppLoginAuthenticationMethod} from "../../orchestration/app-entity.model";
import {IdentityRequestPasswordResetService} from "../identity-request-password-reset.service";
import {IdentityResetPasswordService} from "../identity-reset-password.service";
import {IdentityEntityModel} from "../identity-entity.model";
import {IdentitySearchData, IdentitySearchModel} from "../identity-search.model";

@Component({
    selector: "woa-identification-page",
    templateUrl: "identification-page.component.html",
    providers: [
        provideSearchModel(IdentitySearchModel),
        provideEntityModel(IdentityEntityModel),
        IdentityRequestPasswordResetService,
        IdentityResetPasswordService,
        {provide: Page, useExisting: IdentificationPageComponent}
    ]
})
export class IdentificationPageComponent extends Page implements AfterViewInit {
    @ViewChild(FormComponent) public form: FormComponent;

    IdentificationMode = IdentificationMode;
    AppLoginAuthenticationMethod = AppLoginAuthenticationMethod;
    MessageLevel = MessageLevel;

    mode: IdentificationMode;
    primaryIdentity: string;
    identityPassword: string;
    secondaryMobilePhone: string;
    remember: boolean;
    registeredIdentity: string;
    fullIdentity: string;
    verificationCode: string;

    usedThisApp: boolean;
    usedOtherApp: string;
    loginErrorMessage: string;
    loginErrorParams: any;
    accountHasNoPassword: boolean = false;
    checkRegistrationFailed: boolean = false;
    registrationFailed: boolean = false;
    confirmationFailed: boolean = false;
    verificationId: string;
    verificationCodeDeliveryMethod: VerificationCodeDeliveryMethod;
    verificationCodeDeliveryDetails: string;
    verificationFailed: boolean = false;
    verificationCodeResent: boolean = false;
    requestPasswordResetFailed: boolean = false;
    passwordResetFailed: boolean = false;
    passwordResetCodeResent: boolean = false;

    private appId: number;

    constructor(navigationParams: NavigationParams,
                public sessionModel: SessionModel,
                public identityModel: IdentityModel,
                public identityEntityModel: IdentityEntityModel,
                public loadingService: LoadingService,
                private identitySearchModel: IdentitySearchModel,
                private localConfigurationService: LocalConfigurationService,
                private requestPasswordResetService: IdentityRequestPasswordResetService,
                private resetPasswordService: IdentityResetPasswordService,
                private navigationService: NavigationService,
                private sessionController: SessionController,
                private authService: AuthService,
                private responsiveService: ResponsiveService,
                private changeDetectorRef: ChangeDetectorRef) {
        super();
        this.mode = navigationParams.get("mode");
        this.remember = this.responsiveService.isApp || this.responsiveService.isMobile;
        (async () => this.appId = await this.localConfigurationService.getNumber(Local.APP_ID))();
    }

    get email(): string {
        return this.mode === IdentificationMode.REGISTRATION_DETAILS ? this.identityEntityModel.data.email : this.primaryIdentity;
    }

    set email(email: string) {
        if (this.mode === IdentificationMode.REGISTRATION_DETAILS) {
            this.identityEntityModel.data.email = email ? email.toLowerCase() : email;
        } else {
            this.primaryIdentity = email ? email.toLowerCase() : email;
        }
    }

    get title(): string {
        return this.mode === IdentificationMode.LOGIN ? "woa.heading.identification.loginTitle"
            : this.mode === IdentificationMode.VERIFY_LOGIN ? "woa.heading.identification.verifyLoginTitle"
                : this.mode === IdentificationMode.REGISTER || this.mode === IdentificationMode.REGISTRATION_DETAILS ? "woa.heading.identification.registerTitle"
                    : this.mode === IdentificationMode.CONFIRM ? "woa.heading.identification.confirmTitle"
                        : this.mode === IdentificationMode.VERIFY_REGISTRATION ? "woa.heading.identification.verifyRegistrationTitle"
                            : this.mode === IdentificationMode.ENABLE_ACCOUNT ? "woa.heading.identification.enableAccountTitle"
                                : this.mode === IdentificationMode.FORGOT_PASSWORD ? "woa.heading.identification.forgotPasswordTitle"
                                    : this.mode === IdentificationMode.SET_PASSWORD ? "woa.heading.identification.setPasswordTitle"
                                        : this.mode === IdentificationMode.RESET_PASSWORD ? "woa.heading.identification.resetPasswordTitle"
                                            : undefined;
    }

    get subtitle(): string {
        return this.mode === IdentificationMode.CONFIRM ? "woa.heading.identification.confirmSubtitle"
            : undefined;
    }

    get contentText(): string {
        if ((this.mode === IdentificationMode.ENABLE_ACCOUNT || this.mode === IdentificationMode.FORGOT_PASSWORD) && !this.requestPasswordResetFailed) {
            if (this.mode === IdentificationMode.ENABLE_ACCOUNT && this.primaryIdentity) {
                return "woa.message.identification.enable-account.knownIdentity";
            }
            if (this.identityModel.loginIdentityIsEmail) {
                if (this.mode === IdentificationMode.FORGOT_PASSWORD) {
                    return "woa.message.identification.forgot-password.email";
                }
                return "woa.message.identification.enable-account.unknownIdentity.email";
            }
            if (this.identityModel.loginIdentityIsMobile) {
                if (this.mode === IdentificationMode.FORGOT_PASSWORD) {
                    return "woa.message.identification.forgot-password.mobile";
                }
                return "woa.message.identification.enable-account.unknownIdentity.mobile";
            }
            if (this.identityModel.loginIdentityIsMemberNo) {
                if (this.mode === IdentificationMode.FORGOT_PASSWORD) {
                    return "woa.message.identification.forgot-password.member-no";
                }
                return "woa.message.identification.enable-account.unknownIdentity.member-no";
            }
        } else if ((this.mode === IdentificationMode.VERIFY_LOGIN || this.mode === IdentificationMode.VERIFY_REGISTRATION) && this.verificationId && !this.verificationFailed) {
            return this.verificationCodeDeliveryMethod === VerificationCodeDeliveryMethod.EMAIL
                ? (this.verificationCodeResent ? "woa.message.identification.verificationCodeResentEmail" : "woa.message.identification.verificationCodeSentEmail")
                : (this.verificationCodeResent ? "woa.message.identification.verificationCodeResentMobile" : "woa.message.identification.verificationCodeSentMobile");
        } else if ((this.mode === IdentificationMode.SET_PASSWORD || this.mode === IdentificationMode.RESET_PASSWORD) && !this.passwordResetFailed) {
            return this.passwordResetCodeResent ? "woa.message.identification.passwordResetCodeResent" : "woa.message.identification.passwordResetCodeSent";
        }
        return undefined;
    }

    get alreadyRegisteredMessageText(): string {
        return this.usedThisApp ? (
                this.identityModel.loginIdentityIsEmail ? "woa.message.identification.thisAppUsedEmail"
                    : this.identityModel.loginIdentityIsMobile ? "woa.message.identification.thisAppUsedMobile"
                        : "woa.message.identification.thisAppUsed"
            )
            : this.usedOtherApp ? "woa.message.identification.otherAppUsed"
                : "woa.message.identification.noAppUsed";
    }

    get pageLayoutTranslationParams(): any {
        return {
            "identity": this.primaryIdentity,
            "verificationCodeDeliveryDetails": this.verificationCodeDeliveryDetails
        };
    }

    get submitButtonLabel(): string {
        return this.mode === IdentificationMode.LOGIN ? "woa.button.identification.login"
            : this.mode === IdentificationMode.VERIFY_LOGIN ? "woa.button.identification.verify"
                : this.mode === IdentificationMode.REGISTER ? "woa.button.identification.continue"
                    : this.mode === IdentificationMode.REGISTRATION_DETAILS ? "woa.button.identification.register"
                        : this.mode === IdentificationMode.VERIFY_REGISTRATION ? "woa.button.identification.verify"
                            : this.mode === IdentificationMode.ENABLE_ACCOUNT || this.mode === IdentificationMode.FORGOT_PASSWORD ? "woa.button.identification.sendCode"
                                : this.mode === IdentificationMode.SET_PASSWORD ? "woa.button.identification.setPassword"
                                    : this.mode === IdentificationMode.RESET_PASSWORD ? "woa.button.identification.changePassword"
                                        : undefined;
    }

    get legalMessageSet(): string {
        return this.mode === IdentificationMode.LOGIN || this.mode === IdentificationMode.VERIFY_LOGIN ? "identification.login"
            : this.mode === IdentificationMode.REGISTER || this.mode === IdentificationMode.REGISTRATION_DETAILS ? "identification.register"
                : undefined;
    }

    async ngAfterViewInit(): Promise<void> {
        if (!this.authService.isLoggedIn()) {
            await this.identityModel.loginAsGuest();
        }
        if (this.primaryIdentity && this.identityPassword && !this.form.submitted) {
            // noinspection ES6MissingAwait
            this.login();
        }
    }

    public resetLoginDetails(): void {
        this.primaryIdentity = undefined;
        this.identityPassword = undefined;
        this.secondaryMobilePhone = undefined;
        this.remember = this.responsiveService.isApp || this.responsiveService.isMobile;
        this.form.reset();
    }

    async login(): Promise<void> {
        if (!this.form.valid) {
            return;
        }

        this.registeredIdentity = undefined;
        this.accountHasNoPassword = false;

        // noinspection ES6MissingAwait
        this.loadingService.performWithSpinner(async () => {
            this.fullIdentity = this.primaryIdentity;
            if (this.identityModel.loginRequiresSecondaryMobile) {
                this.fullIdentity += ";" + this.secondaryMobilePhone;
            }
            const loginResult: LoginResult = await this.identityModel.loginAsMember(this.identityModel.loginType, this.remember, this.fullIdentity, this.identityPassword);
            console.log("LOGIN RESULT", loginResult);
            if (loginResult.status === LoginStatus.SUCCESS) {
                // noinspection ES6MissingAwait
                this.navigationService.dismissPage(this, true);
            } else if (loginResult.status === LoginStatus.IDENTITY_VERIFICATION_REQUIRED) {
                this.verificationId = loginResult.verificationId;
                this.verificationCodeDeliveryMethod = loginResult.verificationCodeDeliveryMethod;
                this.verificationCodeDeliveryDetails = loginResult.verificationCodeDeliveryDetails;
                this.mode = IdentificationMode.VERIFY_LOGIN;
            } else if (loginResult.status === LoginStatus.IDENTITY_HAS_NO_PASSWORD) {
                if (this.identityModel.supportsForgotPassword) {
                    this.mode = IdentificationMode.ENABLE_ACCOUNT;
                } else {
                    this.accountHasNoPassword = true;
                }
            } else {
                if (loginResult.error.status === 401) {
                    if (loginResult.error.data.messageKey) {
                        this.loginErrorMessage = loginResult.error.data.messageKey;
                        this.loginErrorParams = loginResult.error.data.messageParams;
                    } else {
                        this.loginErrorMessage = "woa.error.identification.loginFailed";
                    }
                }
                this.resetLoginDetails();
            }
            this.changeDetectorRef.detectChanges();
        });
    }

    verifyLogin(): void {
        if (!this.form.valid) {
            return;
        }

        this.verificationFailed = false;
        // noinspection JSIgnoredPromiseFromCall
        this.loadingService.performWithSpinner(async () => {
            const loginResult: LoginResult = await this.identityModel.loginAsMember(this.identityModel.loginType, this.remember, this.fullIdentity, this.identityPassword, this.verificationId, this.verificationCode);
            if (loginResult.status === LoginStatus.SUCCESS) {
                // noinspection ES6MissingAwait
                this.navigationService.dismissPage(this, true);
            } else {
                this.verificationFailed = true;
                this.verificationCode = undefined;
            }
            this.form.markAsUnsubmitted();
            this.changeDetectorRef.detectChanges();
        });
    }

    resendLoginVerificationCode(): void {
        this.form.markAsUnsubmitted();
        this.verificationFailed = false;
        this.verificationCode = undefined;

        // noinspection JSIgnoredPromiseFromCall
        this.loadingService.performWithSpinner(async () => {
            const loginResult: LoginResult = await this.identityModel.loginAsMember(this.identityModel.loginType, this.remember, this.fullIdentity, this.identityPassword);
            if (loginResult.status === LoginStatus.SUCCESS) { // Unexpected at this point
                // noinspection ES6MissingAwait
                this.navigationService.dismissPage(this, true);
            } else if (loginResult.status === LoginStatus.IDENTITY_VERIFICATION_REQUIRED) {
                this.verificationId = loginResult.verificationId;
                this.verificationCodeDeliveryMethod = loginResult.verificationCodeDeliveryMethod;
                this.verificationCodeDeliveryDetails = loginResult.verificationCodeDeliveryDetails;
                this.verificationCodeResent = true;
            } else {
                this.verificationFailed = true;
            }
            this.changeDetectorRef.detectChanges();
        });
    }

    checkRegistration(): void {
        if (!this.form.valid) {
            return;
        }

        this.registeredIdentity = undefined;
        this.accountHasNoPassword = false;
        this.usedThisApp = false;
        this.usedOtherApp = undefined;
        this.checkRegistrationFailed = false;

        // noinspection JSIgnoredPromiseFromCall
        this.loadingService.performWithSpinner(async () => {
            try {
                await this.identitySearchModel.retrieveByIdentity(this.primaryIdentity, 5000);
                if (this.identitySearchModel.total === 0) {
                    this.identityEntityModel.newEntity();
                    if (this.identityModel.loginIdentityIsEmail) {
                        this.identityEntityModel.data.email = this.primaryIdentity;
                    }
                    if (this.identityModel.loginIdentityIsMobile) {
                        this.identityEntityModel.data.mobilePhone = this.primaryIdentity;
                    }
                    this.mode = IdentificationMode.REGISTRATION_DETAILS;
                } else {
                    this.identitySearchModel.data.some((identitySearchData: IdentitySearchData) => {
                        if (!identitySearchData.passwordSet && this.identityModel.loginAuthenticationMethod === AppLoginAuthenticationMethod.PASSWORD) {
                            if (this.identityModel.supportsForgotPassword) {
                                this.mode = IdentificationMode.ENABLE_ACCOUNT;
                            } else {
                                this.accountHasNoPassword = true;
                            }
                            return true;
                        }

                        this.registeredIdentity = this.primaryIdentity;
                        if (identitySearchData.appId === this.appId) {
                            this.usedThisApp = true;
                            this.mode = IdentificationMode.LOGIN;
                            return true;
                        } else if (identitySearchData.appName && !this.usedOtherApp) {
                            this.usedOtherApp = identitySearchData.appName;
                        }
                        this.mode = IdentificationMode.LOGIN;
                        return false;
                    });
                }
                this.changeDetectorRef.detectChanges();
            } catch (error) {
                console.log(error);
                this.checkRegistrationFailed = true;
            }
        });
    }

    register(): void {
        if (!this.form.valid) {
            return;
        }

        const password: string = this.identityEntityModel.data.password;
        this.registrationFailed = false;
        // noinspection JSIgnoredPromiseFromCall
        this.loadingService.performWithSpinner(async () => {
            try {
                const validationMessages: Array<ValidationMessage> = await this.identityEntityModel.saveEntity();
                this.identityEntityModel.data.password = password;
                if (validationMessages) {
                    this.registrationFailed = true;
                } else {
                    this.confirmLogin();
                }
            } catch (error) {
                if (error.status === 403 && error["conditionNotification"] && error.data.conditionName === "register.registration2FARequired") {
                    this.verificationId = error.data.conditionParams["verificationId"];
                    this.verificationCodeDeliveryMethod = error.data.conditionParams["verificationCodeDeliveryMethod"];
                    this.verificationCodeDeliveryDetails = error.data.conditionParams["verificationCodeDeliveryDetails"];
                    this.mode = IdentificationMode.VERIFY_REGISTRATION;
                    return;
                }
                console.log(error);
                this.registrationFailed = true;
            }
        });
    }

    verifyRegistration(): void {
        if (!this.form.valid) {
            return;
        }

        this.verificationFailed = false;
        this.identityEntityModel.data.verificationId = this.verificationId;
        this.identityEntityModel.data.verificationCode = this.verificationCode;
        // noinspection JSIgnoredPromiseFromCall
        this.loadingService.performWithSpinner(async () => {
            try {
                const validationMessages: Array<ValidationMessage> = await this.identityEntityModel.saveEntity();
                if (validationMessages) {
                    this.verificationFailed = true;
                } else {
                    this.confirmLogin();
                }
            } catch (error) {
                console.log(error);
                this.verificationFailed = true;
            } finally {
                this.verificationId = undefined;
                this.verificationCodeDeliveryMethod = undefined;
                this.verificationCode = undefined;
            }
        });
    }

    resendRegistrationVerificationCode(): void {
        this.form.markAsUnsubmitted();
        this.verificationFailed = false;
        this.verificationCode = undefined;

        // noinspection JSIgnoredPromiseFromCall
        this.loadingService.performWithSpinner(async () => {
            try {
                await this.identityEntityModel.saveEntity();

                // Unexpected at this point
                // noinspection ES6MissingAwait
                this.navigationService.dismissPage(this, true);
            } catch (error) {
                if (error.status === 403 && error["conditionNotification"] && error.data.conditionName === "register.registration2FARequired") {
                    this.verificationId = error.data.conditionParams["verificationId"];
                    this.verificationCodeDeliveryMethod = error.data.conditionParams["verificationCodeDeliveryMethod"];
                    this.verificationCodeDeliveryDetails = error.data.conditionParams["verificationCodeDeliveryDetails"];
                    this.verificationCodeResent = true;
                    return;
                } else {
                    console.log(error);
                    this.verificationFailed = true;
                }
            }
            this.changeDetectorRef.detectChanges();
        });
    }

    requestPasswordReset(): void {
        if (!this.form.valid) {
            return;
        }

        this.requestPasswordResetFailed = false;
        // noinspection JSIgnoredPromiseFromCall
        this.loadingService.performWithSpinner(async () => {
            try {
                await this.requestPasswordResetService.performAction({
                    loginType: this.identityModel.loginType,
                    identity: this.primaryIdentity
                });
                this.mode = this.mode === IdentificationMode.ENABLE_ACCOUNT ? IdentificationMode.SET_PASSWORD : IdentificationMode.RESET_PASSWORD;
            } catch (error) {
                console.log(error);
                this.requestPasswordResetFailed = true;
            }
        });
    }

    resetPassword(): void {
        if (!this.form.valid) {
            return;
        }

        this.passwordResetFailed = false;
        // noinspection JSIgnoredPromiseFromCall
        this.loadingService.performWithSpinner(async () => {
            try {
                await this.resetPasswordService.performAction({
                    loginType: this.identityModel.loginType,
                    identity: this.primaryIdentity,
                    code: this.verificationCode,
                    password: this.identityEntityModel.data.password
                });
                this.confirmLogin();
            } catch (error) {
                console.log(error);
                this.passwordResetFailed = true;
            }
        });
    }

    resendPasswordResetCode(): void {
        this.form.markAsUnsubmitted();
        this.passwordResetFailed = false;
        this.verificationCode = undefined;

        // noinspection JSIgnoredPromiseFromCall
        this.loadingService.performWithSpinner(async () => {
            try {
                await this.requestPasswordResetService.performAction({
                    loginType: this.identityModel.loginType,
                    identity: this.primaryIdentity
                });
                this.passwordResetCodeResent = true;
            } catch (error) {
                console.log(error);
                this.requestPasswordResetFailed = true;
            }
        });
    }

    confirmLogin(): void {
        this.mode = IdentificationMode.CONFIRM;
        this.confirmationFailed = false;
        // noinspection JSIgnoredPromiseFromCall
        this.loadingService.performWithSpinner(async () => {

            // The delay is an attempt to allow signup entitlements to sync to the App before showing the main member screen
            await new Promise((resolve) => setTimeout(resolve, this.sessionModel.currentApp.signupDelayMillis || 3000));

            const loginResult: LoginResult = await this.identityModel.loginAsMember(this.identityModel.loginType, !!this.remember, this.primaryIdentity, this.identityEntityModel.data.password, this.identityEntityModel.data.verificationId, this.identityEntityModel.data.verificationCode);
            if (loginResult.status === LoginStatus.SUCCESS) {
                // noinspection ES6MissingAwait
                this.navigationService.dismissPage(this, true);
            } else {
                this.confirmationFailed = true;
            }
            this.changeDetectorRef.detectChanges();
        });
    }
}



// WEBPACK FOOTER //
// ./src/app/identity/identification-page/identification-page.component.ts